import * as THREE from 'three';
import Weapon from './Weapon.js';
import { WEAPONS } from '../constants/gameSettings.js';

/**
 * Manages the creation, selection, and updating of weapons attached to a bus.
 * Handles asynchronous loading of the bus and individual weapon models.
 */
export default class WeaponManager {
    /**
     * Creates an instance of WeaponManager.
     * @param {object} game - The main game object, expected to have a `scene` property.
     * @param {object} bus - The bus object, expected to have a `mesh` property (THREE.Object3D)
     *                       and potentially a `loadingPromise` (Promise) that resolves when the bus mesh is ready.
     */
    constructor(game, bus, audioManager) {
        if (!game || !game.scene) {
            throw new Error("[WeaponManager] 'game' object with 'scene' property is required.");
        }
        if (!bus) {
            throw new Error("[WeaponManager] 'bus' object is required.");
        }

        if (!audioManager) { // <-- Add check for audioManager
            console.warn("[WeaponManager] AudioManager instance not provided. Weapon sounds will be disabled.");
            // Or throw new Error if it's absolutely required
        }

        this.game = game;
        this.bus = bus;
        this.scene = game.scene;
        this.audioManager = audioManager; // Store the audioManager instance

        /** @type {string[]} List of weapon types available to be created. */
        this.weaponTypes = ['machineGun']; // Add more types like 'rocketLauncher' as needed

        /** @type {Object.<string, Weapon>} Stores Weapon instances, keyed by type. */
        this.weapons = {};

        /** @type {string} The type identifier of the currently selected weapon. */
        this.currentWeaponType = 'machineGun'; // Default weapon

        /** @type {boolean} Flag indicating if the WeaponManager has finished initial setup (waited for bus, started weapon init). */
        this.isReady = false;

        console.log('[WeaponManager] Initializing...');

        // Determine when the bus is ready for weapons to be attached.
        // Prioritize a specific loadingPromise, fall back to checking if the mesh exists.
        const busReadyPromise = this.bus.loadingPromise instanceof Promise
            ? this.bus.loadingPromise
            : (this.bus.mesh ? Promise.resolve() : null);

        if (busReadyPromise) {
            console.log('[WeaponManager] Waiting for bus to be ready...');
            busReadyPromise.then(() => {
                console.log('[WeaponManager] Bus ready.');
                this.performInitialSetup();
            }).catch(error => {
                console.error("[WeaponManager] Error waiting for bus readiness:", error);
                // Decide how to handle this - maybe proceed without weapons?
                // For now, we'll log the error and the manager won't become ready.
            });
        } else if (this.bus.mesh) {
            // If no promise, but mesh exists, assume bus is ready immediately.
             console.log('[WeaponManager] Bus mesh already exists, proceeding with setup.');
             this.performInitialSetup();
        } else {
            console.warn("[WeaponManager] Bus has no loadingPromise and no mesh currently. Weapon initialization might fail or be delayed.");
            // Consider adding a check later or having the game explicitly call init when the bus is ready.
        }
    }

    /**
     * Performs the setup steps after confirming the bus is ready.
     * Initializes weapons and marks the manager as ready.
     * @private
     */
    performInitialSetup() {
        this.initWeapons(); // Start creating weapon instances
        this.isReady = true; // Mark manager as fundamentally ready
        console.log('[WeaponManager] Initial setup complete. Manager is ready.');
        // Note: Individual weapons might still be loading their models asynchronously.
    }


    /**
     * Creates instances of all defined weapon types.
     * Each weapon handles its own asynchronous model loading.
     * Sets the initial visibility based on the current selection.
     * @private
     */
    initWeapons() {
        console.log('[WeaponManager] Initializing weapon instances...');
        for (const type of this.weaponTypes) {
            if (!this.weapons[type]) { // Avoid re-creating if called multiple times
                console.log(`[WeaponManager] Creating weapon of type: ${type}`,this.audioManager);

                // Create the weapon instance. The Weapon class handles its own async loading.
                // Provide a callback to the Weapon constructor to be notified when *this specific weapon* is ready.
                this.weapons[type] = new Weapon(this.scene, this.bus, type, 
                    this.audioManager,
                    (weaponInstance) => {
                    console.log(`[WeaponManager] Weapon '${weaponInstance.type}' model loaded and ready.`);
                    // Set visibility *after* the weapon confirms it's ready and mounted
                    this.setWeaponVisibility(weaponInstance.type);
                });
            }
        }
        console.log('[WeaponManager] All weapon instance creation initiated.');
        // Set initial visibility for weapons that might have loaded synchronously (e.g., fallback geometry)
        this.setActiveWeaponVisuals();
    }

    /**
     * Returns the instance of the currently selected weapon.
     * Returns null if the manager isn't ready or the weapon type doesn't exist.
     * Note: The returned weapon might still be loading its assets (`weapon.isReady === false`).
     * @returns {Weapon | null} The current Weapon instance or null.
     */
    getCurrentWeapon() {
        if (!this.isReady || !this.currentWeaponType) {
            return null;
        }
        return this.weapons[this.currentWeaponType] || null;
    }

    /**
     * Updates the currently active weapon.
     * This should be called every frame in the main game loop.
     * Handles projectile movement, reload timers, etc., for the active weapon.
     * @param {number} deltaTime - Time elapsed since the last frame (in seconds).
     */
    update(deltaTime) {
        // Only proceed if the manager itself has completed initial setup
        if (!this.isReady) {
            return;
        }

        const currentWeapon = this.getCurrentWeapon();

        // Update the active weapon only if it exists AND it has finished its own setup (model loaded, mounted)
        if (currentWeapon && currentWeapon.isReady) {
            currentWeapon.update(deltaTime);
        }
    }

    /**
     * Attempts to fire the currently selected weapon.
     * Checks for manager readiness and individual weapon readiness.
     * The Weapon instance handles fire rate, ammo, and reloading state internally.
     */
    fireCurrentWeapon() {
        if (!this.isReady) {
             // console.log("[WeaponManager] Cannot fire: Manager not ready.");
             return;
        }
        const currentWeapon = this.getCurrentWeapon();
        if (currentWeapon && currentWeapon.isReady) {
            // Check if this is a laser weapon
            if (currentWeapon.projectileType === 'laser') {
                // Check with UIManager if laser is available
                if (this.game.uiManager && !this.game.uiManager.isLaserAvailable()) {
                    // Laser depleted, cannot fire
                    return;
                }
                
                // Deplete laser charge when fired
                if (this.game.uiManager) {
                    const canFire = this.game.uiManager.depleteLaser(25); // Deplete by 25%
                    if (!canFire) return; // Can't fire if laser depleted
                }
            }
            
            currentWeapon.fire();
        } else if (currentWeapon && !currentWeapon.isReady) {
             // console.log(`[WeaponManager] Cannot fire: Weapon '${this.currentWeaponType}' is not ready yet.`);
        } else {
            // console.log("[WeaponManager] Cannot fire: No current weapon selected or available.");
        }
    }

    /**
     * Attempts to reload the currently selected weapon.
     * Checks for manager readiness and individual weapon readiness.
     */
    reloadCurrentWeapon() {
        if (!this.isReady) return;
        const currentWeapon = this.getCurrentWeapon();
        if (currentWeapon && currentWeapon.isReady) {
            currentWeapon.reload();
        } else {
            // console.log("[WeaponManager] Cannot reload: Current weapon not ready or not selected.");
        }
    }

    /**
     * Switches the active weapon to the specified type.
     * Manages the visibility of weapon meshes.
     * @param {string} newType - The type identifier of the weapon to switch to.
     */
    switchWeapon(newType) {
        if (!this.isReady) {
            console.warn("[WeaponManager] Cannot switch weapon: Manager not ready.");
            return;
        }
        if (!this.weaponTypes.includes(newType)) {
            console.warn(`[WeaponManager] Cannot switch: Weapon type '${newType}' is not available.`);
            return;
        }
        if (newType === this.currentWeaponType) {
            // console.log(`[WeaponManager] Weapon type '${newType}' is already selected.`);
            return; // Already selected
        }

        console.log(`[WeaponManager] Switching weapon from '${this.currentWeaponType}' to: '${newType}'`);
        this.currentWeaponType = newType;

        // Update visuals immediately (hide old, show new)
        this.setActiveWeaponVisuals();

        // Optional: Dispatch an event or notify UI
        // this.game.dispatchEvent({ type: 'weaponSwitched', newType: newType });
    }

    /**
     * Sets the visibility of all managed weapon meshes based on the current selection.
     * Ensures only the mesh and mount point of the active weapon are visible.
     * @private
     */
    setActiveWeaponVisuals() {
        // This can be called even if the manager isn't fully "ready",
        // as individual weapons might become ready at different times.
        // console.log(`[WM] Setting active visuals. Current type: ${this.currentWeaponType}`);
        for (const type in this.weapons) {
            this.setWeaponVisibility(type);
        }
    }

    /**
    * Sets the visibility for a specific weapon type based on whether it's currently selected.
    * @param {string} type - The weapon type identifier.
    * @private
    */
    setWeaponVisibility(type) {
        const weapon = this.weapons[type];
        const isActive = (type === this.currentWeaponType);
        // console.log(`[WM] Setting visibility for ${type}. IsActive: ${isActive}`);

        if (weapon) {
            // Only modify visibility if the weapon has confirmed it's ready (mesh/mount exist)
            if (weapon.isReady) {
                 if (weapon.mesh) {
                    weapon.mesh.visible = isActive;
                     // console.log(`[WM] ${type} mesh visibility set to: ${weapon.mesh.visible}`);
                 } else {
                     // console.log(`[WM] ${type} has no mesh to set visibility.`);
                 }
                 // Important: Control the mount point's visibility too, as the mesh is its child.
                 if (weapon.mountPoint) {
                    weapon.mountPoint.visible = isActive;
                     // console.log(`[WM] ${type} mount point visibility set to: ${weapon.mountPoint.visible}`);
                 } else {
                     // console.log(`[WM] ${type} has no mount point to set visibility.`);
                 }
            } else {
                 // console.log(`[WM] Skipping visibility set for ${type}, weapon not ready yet.`);
            }

        }
    }


    /**
     * Gets the ammo status of the currently selected weapon.
     * @returns {{current: number, max: number, isReloading: boolean}} Ammo status object.
     */
    getCurrentAmmoStatus() {
        if (!this.isReady) {
            return { current: 0, max: 0, isReloading: false };
        }
        const currentWeapon = this.getCurrentWeapon();
        if (currentWeapon && currentWeapon.isReady) {
            return currentWeapon.getAmmoStatus();
        }
        // Return default status if weapon isn't ready or doesn't exist
        const weaponSettings = WEAPONS[this.currentWeaponType] || {};
        return {
            current: 0, // Or maybe weaponSettings.ammo if you want to show max before ready?
            max: weaponSettings.ammo || 0,
            isReloading: false
        };
    }

    /**
     * Cleans up resources used by the manager and all its weapons.
     * Should be called when the game ends or the manager is no longer needed.
     */
    dispose() {
        console.log("[WeaponManager] Disposing...");
        for (const type in this.weapons) {
            try {
                this.weapons[type]?.dispose(); // Use optional chaining and let Weapon handle internal cleanup
            } catch (error) {
                console.error(`[WeaponManager] Error disposing weapon ${type}:`, error);
            }
        }
        this.weapons = {};
        this.isReady = false;
        this.currentWeaponType = null;
        console.log("[WeaponManager] Disposed.");
    }
}